import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, formatINR, useCart } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, X, Palette, Wrench, Pipette, Sparkles, RefreshCw, Loader2 } from 'lucide-react';

// Constants + pure helpers extracted so ProductsPage.js can
// shrink and future sub-components can share these. Keep the module
// path (./productsPage/…) alongside — a follow-up refactor will pull
// category-specific rendering blocks (Plywood, Laminate, Hardware) into
// this folder too.
import {
  STANDARD_THICKNESSES,
  BLOCKBOARD_THICKNESSES,
  SIZE_OPTIONS as BUILTIN_SIZE_OPTIONS,
  DOOR_SIZE_OPTIONS,
  DOOR_THICKNESSES,
  SIZE_UNIT_OPTIONS,
} from './productsPage/constants';
import {
  convertSizeToSqFt,
  formatSizeWithUnit,
  getHardwareRate,
  getHardwareUnit,
} from './productsPage/helpers';
import { groupProducts } from './productsPage/groupProducts';
import { useCustomSizePresets, normalizeSizePresets } from './productsPage/customSizePresets';
import { QuickAddMenu } from './productsPage/components/QuickAddMenu';
import { CategoryFilterChips } from './productsPage/components/CategoryFilterChips';
import { ViewToggleAndCart } from './productsPage/components/ViewToggleAndCart';
import { ProductCardHeader } from './productsPage/components/ProductCardHeader';
import { VariantChipSelector } from './productsPage/components/VariantChipSelector';
import { HardwareVariantsBlock } from './productsPage/components/HardwareVariantsBlock';
import { VeneerBlock } from './productsPage/components/VeneerBlock';
import { LaminateBlock } from './productsPage/components/LaminateBlock';
import { HardwareSystemBlock } from './productsPage/components/HardwareSystemBlock';
import {
  DefaultCardVariantsPreview,
  DefaultCardSingleAddButton,
  DefaultCardMultiVariantButtons,
} from './productsPage/components/DefaultCardBlocks';
import { ProductsTableView } from './productsPage/components/ProductsTableView';
import { CustomQuickAddDialog } from './productsPage/components/CustomQuickAddDialog';
// The remaining 9 inline modal dialogs extracted into
// controlled components — parent keeps all state + submit handlers.
import { VariantQuickAddDialog } from './productsPage/components/VariantQuickAddDialog';
// Products-page Quick Adds now reuse the SAME dialogs as the
// cart page (identical fields, size input, math + PDF previews), with an
// extra "Save & Remember" checkbox wired via the `saveRemember` prop.
import {
  QuickAddVeneerDialog,
  QuickAddLaminateDialog,
  QuickAddHardwareDialog,
  QuickAddAdhesiveDialog,
} from '../components/cart/QuickAddDialogs';
import {
  DEFAULT_CART_VENEER_QUICK_ADD,
  DEFAULT_CART_LAMINATE_QUICK_ADD,
  DEFAULT_CART_HARDWARE_QUICK_ADD,
  DEFAULT_CART_ADHESIVE_QUICK_ADD,
} from '../components/cart/constants';
import { joinWithHyphen } from '../lib/joinWithHyphen';
import { PlywoodMultiSelectDialog } from './productsPage/components/PlywoodMultiSelectDialog';
import { LaminateCodeSelectorDialog } from './productsPage/components/LaminateCodeSelectorDialog';
import { HardwareVariantSelectorDialog } from './productsPage/components/HardwareVariantSelectorDialog';
import { HardwareSystemSelectorDialog } from './productsPage/components/HardwareSystemSelectorDialog';
import { VeneerSizeSelectorDialog } from './productsPage/components/VeneerSizeSelectorDialog';

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // User-added custom size presets (e.g. 8.5x4,
  // 9x3.5) fetched from /settings and merged with the built-in
  // SIZE_OPTIONS. Shadowing the imported name so downstream code
  // reads the merged list transparently without any use-site edits.
  const _customPresetsRaw = useCustomSizePresets();
  const SIZE_OPTIONS = useMemo(() => {
    const extras = normalizeSizePresets(
      _customPresetsRaw,
      BUILTIN_SIZE_OPTIONS.map(s => s.value.toLowerCase())
    );
    return [...BUILTIN_SIZE_OPTIONS, ...extras];
  }, [_customPresetsRaw]);
  const navigate = useNavigate();
  const { addItem, addQuickVeneer, replaceItem, items: cartItems } = useCart();
  
  // Replace mode detection
  const isReplaceMode = searchParams.get('replace') === 'true';
  const replaceCategoryFilter = searchParams.get('category') || '';
  const replaceCartId = sessionStorage.getItem('replaceCartId');

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  // When set (via Dashboard search-result click), Products page shows ONLY
  // this single product as a card and skips all other filters.
  const [filterProductId, setFilterProductId] = useState(searchParams.get('productId') || '');
  const [filterBrand, setFilterBrand] = useState(searchParams.get('brand') || '');
  // If URL has search/productId param, don't set default category (search across all)
  // If in replace mode, use the category from URL
  const urlSearch = searchParams.get('search');
  const urlProductId = searchParams.get('productId');
  const urlCategory = searchParams.get('category');
  const [filterCategory, setFilterCategory] = useState(
    isReplaceMode ? (urlCategory || '') : ((urlSearch || urlProductId) ? '' : (urlCategory || 'Plywood'))
  );
  const [filterSubcategory, setFilterSubcategory] = useState('');
  // `showFilters` state removed alongside the broken Filter
  // button — the toggle panel it controlled was unreachable/redundant
  // with the category chips + URL-driven brand filter.

  // Sync state with URL params when they change (e.g., navigation from Dashboard)
  // DO NOT clobber `searchInput` when the URL update was our
  // own doing (debounced write from handleSearchInput). We tag each
  // self-write with lastUrlWriteRef; if the current URL value equals
  // that tag, we skip the setSearchInput sync so an in-flight typing
  // session keeps whatever characters the user has typed since the
  // debounce fired.
  useEffect(() => {
    const newSearch = searchParams.get('search') || '';
    const newProductId = searchParams.get('productId') || '';
    const newCategory = searchParams.get('category') || '';
    const newBrand = searchParams.get('brand') || '';
    const quickAddType = searchParams.get('quickadd');
    const quickAddCode = searchParams.get('code');

    setSearch(newSearch);
    // Only sync the CONTROLLED INPUT from the URL when the URL change
    // did NOT originate from our own recent write. External nav still
    // resets the input as before; self-writes are ignored.
    if (newSearch !== lastUrlWriteRef.current) {
      setSearchInput(newSearch);
    }
    setFilterProductId(newProductId);
    setFilterBrand(newBrand);

    // If there's a search query or productId, clear the category filter to search across all
    if (newSearch || newProductId) {
      setFilterCategory('');
      setFilterSubcategory('');
    } else if (newCategory) {
      setFilterCategory(newCategory);
    }
    
    // Handle Quick Add from Dashboard
    if (quickAddType && quickAddCode) {
      setTimeout(() => {
        if (quickAddType === 'laminate') {
          setLaminateQuickAddData(d => ({ ...d, category: 'Laminates', product_code: quickAddCode.toUpperCase(), saveToDatabase: true }));
          setShowLaminateQuickAdd(true);
        } else if (quickAddType === 'highlighter') {
          setLaminateQuickAddData(d => ({ ...d, category: 'Highlighters', product_code: quickAddCode.toUpperCase(), saveToDatabase: true }));
          setShowLaminateQuickAdd(true);
        } else if (quickAddType === 'hardware') {
          setHardwareQuickAddData(d => ({ ...d, product_code: quickAddCode.toUpperCase(), saveToDatabase: true }));
          setShowHardwareQuickAdd(true);
        } else if (quickAddType === 'adhesive') {
          setAdhesiveQuickAddData(d => ({ ...d, product_code: quickAddCode.toUpperCase(), saveToDatabase: true }));
          setShowAdhesiveQuickAdd(true);
        }
        // Clear the URL params after handling
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('quickadd');
        newUrl.searchParams.delete('code');
        window.history.replaceState({}, '', newUrl);
      }, 500);
    }
    // Don't reset to 'Plywood' if URL has no params - let existing state persist
    // Only reset to 'Plywood' on initial mount (handled in useState initializer)
  }, [searchParams]);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Live search suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  // Tracks the last `search` value THIS component wrote to
  // the URL itself (via the debounced handleSearchInput / suggestion
  // pick / clear). Used to distinguish "URL changed because of my own
  // typing echoing back" from "URL changed because of external
  // navigation (Dashboard link, back button, etc.)". The former MUST
  // NOT clobber `searchInput` — that was the source of the last-1-2-
  // characters-vanish glitch reported while typing "locks".
  const lastUrlWriteRef = useRef(null);

  // Quick add state
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedThickness, setSelectedThickness] = useState(null);
  const [selectedSize, setSelectedSize] = useState('8x4');
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1'); // Separate state for input field

  // Laminate code selector state
  const [selectedLaminateGroup, setSelectedLaminateGroup] = useState(null);
  const [selectedLaminateCode, setSelectedLaminateCode] = useState(null);
  const [selectedLaminateSize, setSelectedLaminateSize] = useState('');
  const [laminateQuantity, setLaminateQuantity] = useState(1);
  const [laminateQuantityInput, setLaminateQuantityInput] = useState('1');
  const [laminateCodeSearch, setLaminateCodeSearch] = useState(''); // Search filter for codes

  // Hardware variant selector state
  const [selectedHardwareGroup, setSelectedHardwareGroup] = useState(null);
  const [selectedHardwareVariant, setSelectedHardwareVariant] = useState(null);
  const [selectedHardwareRateType, setSelectedHardwareRateType] = useState('');
  const [hardwareQuantity, setHardwareQuantity] = useState(1);
  const [hardwareQuantityInput, setHardwareQuantityInput] = useState('1');
  // Multi-select cart for Hardware (Screws / Nails / Lipping-Patti / Fasteners / Moulding)
  // — lets staff pick several variants (different sizes / rate types) before
  // committing them all to the quotation in one go (mirrors the Plywood and
  // Tandem multi-select UX).
  const [hardwareCart, setHardwareCart] = useState([]);

  // Hardware System variant selector state (for Tandems, Hinges, etc.)
  const [selectedHardwareSystemGroup, setSelectedHardwareSystemGroup] = useState(null);
  const [selectedHardwareSystemVariant, setSelectedHardwareSystemVariant] = useState(null);
  const [hardwareSystemQuantity, setHardwareSystemQuantity] = useState(1);
  const [hardwareSystemQuantityInput, setHardwareSystemQuantityInput] = useState('1');
  // Multi-select cart for hardware system (for adding multiple variants at once)
  const [hardwareSystemCart, setHardwareSystemCart] = useState([]);
  // For Tandem: selected depth filter
  const [selectedTandemDepth, setSelectedTandemDepth] = useState(null);
  // For Hinge: selected mount type (Clip-on/Slide-on)
  const [selectedHingeMountType, setSelectedHingeMountType] = useState(null);
  // For Telescopic: selected feature type (Regular, Soft Close, Push to Open, etc.)
  const [selectedTelescopicFeature, setSelectedTelescopicFeature] = useState(null);
  // For Sliding: selected sliding type (Top Sliding, Bottom Sliding, etc.)
  const [selectedSlidingType, setSelectedSlidingType] = useState(null);

  // Multi-size selection for Plywood/Blockboard/MDF (with Add to Selection cart)
  const [showPlywoodMultiSelect, setShowPlywoodMultiSelect] = useState(false);
  const [plywoodMultiSelectProduct, setPlywoodMultiSelectProduct] = useState(null);
  const [plywoodMultiSelectThickness, setPlywoodMultiSelectThickness] = useState(null);
  const [plywoodSelectedSize, setPlywoodSelectedSize] = useState(null); // e.g., '8x4'
  const [plywoodQuantity, setPlywoodQuantity] = useState(1);
  const [plywoodQuantityInput, setPlywoodQuantityInput] = useState('1');
  const [plywoodCart, setPlywoodCart] = useState([]); // [{ size: '8x4', quantity: 2, rate: 1200 }, ...]
  const [plywoodStockInfo, setPlywoodStockInfo] = useState(null); // Stock info for selected size

  // Custom Quick Add state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddData, setQuickAddData] = useState({
    name: '',
    product_code: '',     // Optional product code - shown instead of name in customer PDF
    rate: '',
    rate_type: 'per piece',  // Default rate type
    custom_rate_type: '',    // Custom rate type (if "custom" is selected)
    category: 'Others',       // Default category for one-off items (canonical bucket in PDF)
    sizeLength: '8',  // Length dimension
    sizeWidth: '4',   // Width dimension
    sizeUnit: 'feet', // Unit: mm, cm, inch, feet, meter
    specs: '',
    quantity: 1,
    quantityInput: '1',
  });
  
  // Laminate/Highlighter Quick Add with Memory — SAME field shape as the
  // cart-page dialog (description/thickness/size string/uom/show_brand),
  // plus saveToDatabase for the Save & Remember checkbox.
  const [showLaminateQuickAdd, setShowLaminateQuickAdd] = useState(false);
  const [laminateQuickAddData, setLaminateQuickAddData] = useState({ ...DEFAULT_CART_LAMINATE_QUICK_ADD, saveToDatabase: true });
  
  // Hardware Quick Add state
  const [showHardwareQuickAdd, setShowHardwareQuickAdd] = useState(false);
  const [hardwareQuickAddData, setHardwareQuickAddData] = useState({ ...DEFAULT_CART_HARDWARE_QUICK_ADD, saveToDatabase: true });
  
  // Adhesive Quick Add state
  const [showAdhesiveQuickAdd, setShowAdhesiveQuickAdd] = useState(false);
  // Active secondary rate selection for an adhesive product (when the
  // product has both a primary and secondary rate, e.g. per piece + per
  // bundle, this stores 'primary' or 'secondary' for the open dialog).
  const [adhesiveRateChoice, setAdhesiveRateChoice] = useState('primary');
  const [adhesiveQuickAddData, setAdhesiveQuickAddData] = useState({ ...DEFAULT_CART_ADHESIVE_QUICK_ADD, saveToDatabase: true });
  
  // Quick Add Menu state (for dropdown)
  const [showQuickAddMenu, setShowQuickAddMenu] = useState(false);

  // Calculate sq ft from custom size
  const calculateSqFt = () => {
    const length = parseFloat(quickAddData.sizeLength) || 0;
    const width = parseFloat(quickAddData.sizeWidth) || 0;
    const unitOption = SIZE_UNIT_OPTIONS.find(u => u.value === quickAddData.sizeUnit);
    const toFeet = unitOption?.toFeet || 1;
    
    // Convert both dimensions to feet and calculate area
    const lengthInFeet = length * toFeet;
    const widthInFeet = width * toFeet;
    return lengthInFeet * widthInFeet;
  };

  // "8x4 ft" / "84x36 in" → "8x4" / "84x36" for catalog available_sizes.
  const stripSizeUnit = (s) => String(s || '').replace(/\s*(ft|feet|in|inch|inches|cm|mm)\s*$/i, '').trim();

  // Quick Add Veneer state (for Products page) — same shape as the cart
  // dialog + saveToDatabase (fixes: veneers never saved from Products).
  const [showQuickVeneerDialog, setShowQuickVeneerDialog] = useState(false);
  const [quickVeneerData, setQuickVeneerData] = useState({ ...DEFAULT_CART_VENEER_QUICK_ADD, saveToDatabase: true });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 200 }; // Get more to group them
      if (filterProductId) {
        // Show only one specific product (from Dashboard search-result click)
        params.product_id = filterProductId;
      } else if (search) {
        params.search = search;
        // When searching, don't filter by category - search across ALL
      } else {
        // Only apply category filter when NOT searching
        if (filterCategory) params.category = filterCategory;
        if (filterSubcategory) params.subcategory = filterSubcategory;
      }
      if (filterBrand) params.brand = filterBrand;
      const res = await api.getProducts(params);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterProductId, filterBrand, filterCategory, filterSubcategory]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    api.getBrands().then(r => setBrands(r.data)).catch(console.error);
    api.getCategories().then(r => setCategories(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (filterCategory) {
      api.getSubcategories(filterCategory).then(r => setSubcategories(r.data)).catch(console.error);
    } else {
      setSubcategories([]);
    }
  }, [filterCategory]);

  // Fetch stock info when plywood size is selected
  useEffect(() => {
    const fetchStockInfo = async () => {
      if (!plywoodSelectedSize || !plywoodMultiSelectProduct || !selectedGroup) {
        setPlywoodStockInfo(null);
        return;
      }
      
      // Only fetch for stock-tracked categories
      const stockCategories = ['Plywood', 'Blockboard', 'Doors', 'MDF', 'WPC/PVC'];
      const category = selectedGroup.category || selectedGroup.main_category;
      if (!stockCategories.includes(category)) {
        setPlywoodStockInfo(null);
        return;
      }
      
      try {
        const res = await api.getStockForProduct({
          brand_name: selectedGroup.brand,
          category: category,
          thickness: plywoodMultiSelectThickness,
          size: plywoodSelectedSize
        });
        setPlywoodStockInfo(res.data);
      } catch (err) {
        console.error('Failed to fetch stock info', err);
        setPlywoodStockInfo(null);
      }
    };
    
    fetchStockInfo();
  }, [plywoodSelectedSize, plywoodMultiSelectProduct, selectedGroup, plywoodMultiSelectThickness]);

  // Live search with debounce — `searchInput` drives the controlled <Input className="rounded-xl bg-heda-cream/50">
  // so typing is instant; `search` (which actually triggers the heavy
  // /api/products fetch via the fetchProducts useCallback) is updated only
  // after 300ms of inactivity. Without this split, every keystroke was
  // re-fetching the full product grid and causing visible typing lag.
  const handleSearchInput = (value) => {
    setSearchInput(value);
    // If user starts typing, leave the single-product view
    if (filterProductId) {
      setFilterProductId('');
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      // Clear search params + actual search query when input is empty
      if (value.length === 0) {
        setSearch('');
        setPage(1);
        const params = {};
        if (filterBrand) params.brand = filterBrand;
        if (filterCategory) params.category = filterCategory;
        // Tag this self-write so the URL sync effect skips it.
        lastUrlWriteRef.current = '';
        setSearchParams(params);
      }
      return;
    }

    // Debounce both suggestion fetch AND the main search after 300ms idle
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.getProducts({ search: value, limit: 10 });
        // Group suggestions by brand + subcategory + grade
        const grouped = {};
        res.data.products.forEach(p => {
          const key = `${p.brand_name}|||${p.subcategory || p.product_name}|||${p.grade || ''}`;
          if (!grouped[key]) {
            grouped[key] = {
              brand: p.brand_name,
              name: p.subcategory || p.product_name,
              grade: p.grade || '',
              product_code: p.product_code || '',
              category: p.main_category,
              variants: [],
            };
          }
          grouped[key].variants.push(p);
          if (!grouped[key].product_code && p.product_code) {
            grouped[key].product_code = p.product_code;
          }
        });
        setSuggestions(Object.values(grouped).slice(0, 8));
        setShowSuggestions(true);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }

      // Now commit the search (this triggers the main fetchProducts via
      // the search dep) and reflect in the URL.
      setSearch(value);
      setPage(1);
      const params = { search: value };
      if (filterBrand) params.brand = filterBrand;
      if (filterCategory) params.category = filterCategory;
      // Tag this self-write so the URL sync effect skips it.
      lastUrlWriteRef.current = value;
      setSearchParams(params);
    }, 300);
  };

  // Select a suggestion
  const selectSuggestion = (suggestion) => {
    // Set search to the selected item
    const searchText = `${suggestion.brand} ${suggestion.name}`.trim();
    setSearchInput(searchText);
    setSearch(searchText);
    setShowSuggestions(false);

    // Trigger search
    setPage(1);
    const params = { search: searchText };
    if (filterCategory) params.category = filterCategory;
    // Tag this self-write so the URL sync effect skips it.
    lastUrlWriteRef.current = searchText;
    setSearchParams(params);
  };

  // Group products by brand + subcategory + grade for card view
  // Logic extracted to ./productsPage/groupProducts.js
  // so the mega-component can shrink and the grouping rules are
  // unit-tested in isolation.
  const groupedProducts = useMemo(() => groupProducts(products), [products]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    const params = {};
    if (search) {
      params.search = search;
      // Clear category filter when searching to show results across all categories
      // This will trigger fetchProducts via useEffect
      setFilterCategory('');
      setFilterSubcategory('');
    } else {
      // No search term, apply current category filter
      if (filterCategory) params.category = filterCategory;
    }
    if (filterBrand) params.brand = filterBrand;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setFilterProductId('');
    setFilterBrand('');
    setFilterCategory('');
    setFilterSubcategory('');
    setPage(1);
    setSearchParams({});
  };

  // Helper: Add or Replace product (for comparison mode)
  const addOrReplaceProduct = (product, config) => {
    if (isReplaceMode && replaceCartId) {
      // Replace mode: replace the existing item
      replaceItem(replaceCartId, product, config);
      toast.success(`Replaced with ${product.brand_name} ${product.subcategory || product.product_name}`);
      
      // Navigate back to cart with comparison state
      const editId = sessionStorage.getItem('replaceEditId');
      const isComparison = sessionStorage.getItem('replaceIsComparison') === 'true';
      const parentId = sessionStorage.getItem('replaceParentId');
      const subject = sessionStorage.getItem('replaceSubject');
      
      // Clear sessionStorage
      sessionStorage.removeItem('replaceCartId');
      sessionStorage.removeItem('replaceCategory');
      sessionStorage.removeItem('replaceEditId');
      sessionStorage.removeItem('replaceIsComparison');
      sessionStorage.removeItem('replaceParentId');
      sessionStorage.removeItem('replaceSubject');
      
      // Build the cart URL with all necessary params
      let cartUrl = '/cart';
      const params = new URLSearchParams();
      if (editId) params.set('edit', editId);
      if (isComparison) params.set('comparison', 'true');
      if (parentId) params.set('parentId', parentId);
      if (subject) params.set('subject', subject);
      
      if (params.toString()) {
        cartUrl += '?' + params.toString();
      }
      
      navigate(cartUrl);
    } else {
      // Normal add mode
      addItem(product, config);
    }
  };

  // Navigate back to cart (for replace mode cancel)
  const navigateBackToCart = () => {
    const editId = sessionStorage.getItem('replaceEditId');
    const isComparison = sessionStorage.getItem('replaceIsComparison') === 'true';
    const parentId = sessionStorage.getItem('replaceParentId');
    const subject = sessionStorage.getItem('replaceSubject');
    
    // Clear sessionStorage
    sessionStorage.removeItem('replaceCartId');
    sessionStorage.removeItem('replaceCategory');
    sessionStorage.removeItem('replaceEditId');
    sessionStorage.removeItem('replaceIsComparison');
    sessionStorage.removeItem('replaceParentId');
    sessionStorage.removeItem('replaceSubject');
    
    let cartUrl = '/cart';
    const params = new URLSearchParams();
    if (editId) params.set('edit', editId);
    if (isComparison) params.set('comparison', 'true');
    if (parentId) params.set('parentId', parentId);
    if (subject) params.set('subject', subject);
    
    if (params.toString()) {
      cartUrl += '?' + params.toString();
    }
    
    navigate(cartUrl);
  };

  // Check if a thickness is available for a product group
  const isThicknessAvailable = (group, thickness) => {
    const normalized = thickness.toUpperCase();
    // Check for exact match or variations like 04MM, 06MM
    return Object.keys(group.thicknessMap).some(t => {
      const t_normalized = t.replace(/^0+/, '');
      return t_normalized === normalized || t === normalized || 
             t.replace('MM', '') === normalized.replace('MM', '');
    });
  };

  // Get product for a specific thickness
  const getProductForThickness = (group, thickness) => {
    // Handle direct add case with 'default' key
    if (thickness === 'default' && group.thicknessMap['default']) {
      return group.thicknessMap['default'];
    }
    const normalized = thickness.toUpperCase();
    for (const [t, product] of Object.entries(group.thicknessMap)) {
      const t_normalized = t.replace(/^0+/, '');
      if (t_normalized === normalized || t === normalized || 
          t.replace('MM', '') === normalized.replace('MM', '')) {
        return product;
      }
    }
    return null;
  };

  // Handle thickness selection
  const handleThicknessSelect = (group, thickness) => {
    const product = getProductForThickness(group, thickness);
    if (product) {
      // For all per-sq-ft thickness bulk categories — open the multi-size
      // "Add to Selection" cart so user can add several sizes at once
      // (matches the Plywood UX user requested for Doors / WPC / PVC / MDF).
      if (['Plywood', 'Blockboard', 'MDF', 'Wooden Flooring', 'WPC/PVC', 'Doors'].includes(group.category) && group.rate_type === 'per sq ft') {
        setPlywoodMultiSelectProduct(product);
        setPlywoodMultiSelectThickness(thickness);
        setSelectedGroup(group);
        // Reset cart state
        setPlywoodSelectedSize(null);
        setPlywoodQuantity(1);
        setPlywoodQuantityInput('1');
        setPlywoodCart([]);
        setShowPlywoodMultiSelect(true);
      } else {
        // For other products, use the regular single-size dialog
        setSelectedGroup(group);
        setSelectedThickness(thickness);
      }
    }
  };
  
  // Sort sizes for Plywood: group by width (x4, x4, x4, then x3, x3, x3)
  // sortPlywoodSizes now imported from ./productsPage/helpers
  
  // Add item to plywood cart
  const addToPlywoodCart = () => {
    if (!plywoodSelectedSize || !plywoodMultiSelectProduct) return;
    const qty = parseInt(plywoodQuantityInput) || 1;
    const sourceOpts = selectedGroup?.isDoor ? DOOR_SIZE_OPTIONS : SIZE_OPTIONS;
    let sizeOpt = sourceOpts.find(s => s.value === plywoodSelectedSize);
    if (!sizeOpt) {
      // Custom available_size that isn't in the constants — derive from numbers.
      const parts = String(plywoodSelectedSize).split('x').map(p => parseFloat(p));
      const sqft = (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]))
        ? (selectedGroup?.isDoor ? (parts[0] * parts[1]) / 144 : parts[0] * parts[1])
        : 32;
      const label = selectedGroup?.isDoor
        ? `${parts[0]}" x ${parts[1]}"`
        : `${parts[0]} x ${parts[1]}`;
      sizeOpt = { value: plywoodSelectedSize, label, sqft };
    }
    const sheetRate = (plywoodMultiSelectProduct.base_rate || 0) * (sizeOpt?.sqft || 32);
    
    // Check if already in cart
    const existingIdx = plywoodCart.findIndex(item => item.size === plywoodSelectedSize);
    if (existingIdx >= 0) {
      // Update quantity
      const newCart = [...plywoodCart];
      newCart[existingIdx].quantity += qty;
      setPlywoodCart(newCart);
    } else {
      setPlywoodCart([...plywoodCart, {
        size: plywoodSelectedSize,
        sizeLabel: sizeOpt?.label || plywoodSelectedSize.toUpperCase(),
        sqft: sizeOpt?.sqft || 32,
        quantity: qty,
        rate: sheetRate,
      }]);
    }
    
    // Reset selection for next item
    setPlywoodSelectedSize(null);
    setPlywoodQuantity(1);
    setPlywoodQuantityInput('1');
    toast.success(`Added ${plywoodSelectedSize.toUpperCase()} (${qty} sheets) to selection`);
  };
  
  // Remove item from plywood cart
  const removeFromPlywoodCart = (index) => {
    const newCart = plywoodCart.filter((_, i) => i !== index);
    setPlywoodCart(newCart);
  };
  
  // Add all plywood cart items to quotation
  const handlePlywoodMultiAdd = () => {
    if (!plywoodMultiSelectProduct || !selectedGroup) return;
    
    if (plywoodCart.length === 0) {
      toast.error('Please add at least one size to selection');
      return;
    }
    
    if (isReplaceMode && replaceCartId) {
      // In replace mode, only use the first item from plywood cart
      const firstItem = plywoodCart[0];
      addOrReplaceProduct(plywoodMultiSelectProduct, {
        size: firstItem.size,
        quantity: firstItem.quantity,
        unit: 'NOS',
        calculatedRate: firstItem.rate,
        thickness: plywoodMultiSelectThickness,
        baseRate: plywoodMultiSelectProduct.base_rate,
      });
    } else {
      // Normal mode: add all items
      plywoodCart.forEach(item => {
        addItem(plywoodMultiSelectProduct, {
          size: item.size,
          quantity: item.quantity,
          unit: 'NOS',
          calculatedRate: item.rate,
          thickness: plywoodMultiSelectThickness,
        });
      });
      
      const totalItems = plywoodCart.reduce((sum, item) => sum + item.quantity, 0);
      toast.success(`Added ${plywoodCart.length} size(s) (${totalItems} sheets) to quotation`);
    }
    
    closePlywoodMultiSelect();
  };

  // Close + fully reset the plywood multi-select dialog state
  const closePlywoodMultiSelect = () => {
    setShowPlywoodMultiSelect(false);
    setPlywoodMultiSelectProduct(null);
    setPlywoodMultiSelectThickness(null);
    setSelectedGroup(null);
    setPlywoodSelectedSize(null);
    setPlywoodQuantity(1);
    setPlywoodQuantityInput('1');
    setPlywoodCart([]);
  };

  // Directly add single selected size to quotation without going through
  // the inner selection cart (extracted from the dialog's inline handler).
  const handlePlywoodSingleAdd = () => {
    if (!plywoodMultiSelectProduct || !selectedGroup || !plywoodSelectedSize) return;

    const sourceOpts = selectedGroup?.isDoor ? DOOR_SIZE_OPTIONS : SIZE_OPTIONS;
    const selectedSizeOption = sourceOpts.find(s => s.value === plywoodSelectedSize) ||
      { value: plywoodSelectedSize, label: plywoodSelectedSize, sqft: 32 };
    const sqFt = convertSizeToSqFt(selectedSizeOption.value, plywoodMultiSelectProduct.size_unit || 'feet');
    const rate = (plywoodMultiSelectProduct.base_rate || 0) * sqFt;
    const qty = parseInt(plywoodQuantityInput) || 1;

    if (isReplaceMode && replaceCartId) {
      addOrReplaceProduct(plywoodMultiSelectProduct, {
        size: selectedSizeOption.value,
        quantity: qty,
        unit: 'NOS',
        calculatedRate: rate,
        thickness: plywoodMultiSelectThickness,
        baseRate: plywoodMultiSelectProduct.base_rate,
      });
    } else {
      addItem(plywoodMultiSelectProduct, {
        size: selectedSizeOption.value,
        quantity: qty,
        unit: 'NOS',
        calculatedRate: rate,
        thickness: plywoodMultiSelectThickness,
      });
      toast.success(`Added ${qty} sheet(s) to quotation`);
    }

    closePlywoodMultiSelect();
    setPlywoodStockInfo(null);
  };

  // Calculate rate based on size
  // convertSizeToSqFt + formatSizeWithUnit now imported from ./productsPage/helpers

  const getCalculatedRate = (product, group = null) => {
    if (!product) return 0;
    if (product.rate_type === 'per sq ft') {
      // Use door sizes if it's a door, otherwise use standard sizes
      const sizeOptions = (group?.isDoor || product.main_category === 'Doors') ? DOOR_SIZE_OPTIONS : SIZE_OPTIONS;
      const sizeOpt = sizeOptions.find(s => s.value === selectedSize);
      return product.base_rate * (sizeOpt?.sqft || 32);
    }
    return product.base_rate;
  };

  // Quick add to cart
  const handleQuickAdd = () => {
    if (!selectedGroup || !selectedThickness) return;
    const product = getProductForThickness(selectedGroup, selectedThickness);
    if (!product) return;

    // For Adhesives with a secondary rate (e.g. per piece + per bundle), use
    // the rate the user picked via the chip selector. Defaults to primary.
    const useSecondary = product.main_category === 'Adhesives'
      && adhesiveRateChoice === 'secondary'
      && (parseFloat(product.secondary_base_rate) || 0) > 0;
    const effectiveProduct = useSecondary
      ? { ...product, base_rate: parseFloat(product.secondary_base_rate), rate_type: product.secondary_rate_type || product.rate_type }
      : product;

    const calcRate = getCalculatedRate(effectiveProduct, selectedGroup);
    const sizeOptions = selectedGroup.isDoor ? DOOR_SIZE_OPTIONS : SIZE_OPTIONS;
    const sizeOpt = sizeOptions.find(s => s.value === selectedSize);
    // Adhesives + Hardware sold by weight/length allow fractional quantities.
    const isFractional = ['per kg', 'per running feet', 'per ltr', 'per gms', 'per ml'].includes(effectiveProduct.rate_type);
    const finalQuantity = Math.max(isFractional ? 0.01 : 1, (isFractional ? parseFloat(quantityInput) : parseInt(quantityInput)) || 1);

    // For doors, show size in inches format
    const displaySize = selectedGroup.isDoor ? `${selectedSize}"` : selectedSize;

    addOrReplaceProduct(effectiveProduct, {
      size: displaySize,
      quantity: finalQuantity,
      unit: effectiveProduct.rate_type === 'per sq ft' ? 'NOS' : 'NOS',
      calculatedRate: calcRate,
      thickness: effectiveProduct.thickness,
      baseRate: effectiveProduct.base_rate,
    });

    if (!isReplaceMode) {
      toast.success(`Added ${effectiveProduct.brand_name} ${effectiveProduct.subcategory || effectiveProduct.product_name} (${effectiveProduct.thickness}) to quotation`);
    }
    setSelectedGroup(null);
    setSelectedThickness(null);
    setQuantity(1);
    setQuantityInput('1');
    setAdhesiveRateChoice('primary');
  };

  // Handle direct quick add for non-plywood (opens dialog with quantity)
  const handleDirectQuickAdd = (product) => {
    setSelectedGroup({ 
      brand: product.brand_name, 
      name: product.subcategory || product.product_name, 
      variants: [product], 
      thicknessMap: { [product.thickness || 'default']: product }, 
      category: product.main_category, 
      rate_type: product.rate_type,
      isDirectAdd: true // Flag to indicate this is a direct add
    });
    setSelectedThickness(product.thickness || 'default');
    setQuantity(1);
    setQuantityInput('1');
  };

  // Handle laminate group click - open code selector dialog
  const handleLaminateClick = (group) => {
    setSelectedLaminateGroup(group);
    setLaminateCodeSearch(''); // Reset search
    // Set default code to first one
    if (group.codes_and_rates && group.codes_and_rates.length > 0) {
      const firstCode = group.codes_and_rates[0];
      setSelectedLaminateCode(firstCode);
      // Set default size to first available FOR THIS CODE (if code has specific sizes)
      if (firstCode.sizes && firstCode.sizes.length > 0) {
        setSelectedLaminateSize(firstCode.sizes[0]);
      } else if (group.available_sizes && group.available_sizes.length > 0) {
        setSelectedLaminateSize(group.available_sizes[0]);
      } else {
        setSelectedLaminateSize('8x4');
      }
    } else {
      setSelectedLaminateCode(null);
      setSelectedLaminateSize('8x4');
    }
    setLaminateQuantity(1);
    setLaminateQuantityInput('1');
  };

  // Handle laminate code selection - update size based on selected code's available sizes
  const handleLaminateCodeSelect = (cr) => {
    setSelectedLaminateCode(cr);
    // Update size to first available for this specific code
    if (cr.sizes && cr.sizes.length > 0) {
      // If current size is not available for this code, switch to first available
      if (!cr.sizes.includes(selectedLaminateSize)) {
        setSelectedLaminateSize(cr.sizes[0]);
      }
    } else if (selectedLaminateGroup?.available_sizes && selectedLaminateGroup.available_sizes.length > 0) {
      // Fallback to collection-level sizes
      if (!selectedLaminateGroup.available_sizes.includes(selectedLaminateSize)) {
        setSelectedLaminateSize(selectedLaminateGroup.available_sizes[0]);
      }
    }
  };

  // getAvailableSizesForCode + code search filtering moved into
  // ./productsPage/components/LaminateCodeSelectorDialog.

  // Handle laminate add to cart
  const handleLaminateAdd = () => {
    if (!selectedLaminateGroup || !selectedLaminateCode) return;
    
    const variant = selectedLaminateGroup.variants[0];
    const finalQuantity = Math.max(1, parseInt(laminateQuantityInput) || 1);
    
    // Create product object for cart
    const codeRateType = selectedLaminateCode.rate_type || selectedLaminateGroup.rate_type || 'per sheet';
    const codeSizeUnit = selectedLaminateCode.size_unit || selectedLaminateGroup.size_unit || 'feet';
    
    // Calculate rate based on rate_type
    // Defensive: if code rate is missing/0, fall back to group's base_rate or min_rate
    const codeRate = parseFloat(selectedLaminateCode.rate) > 0 
      ? parseFloat(selectedLaminateCode.rate)
      : (parseFloat(selectedLaminateGroup.base_rate) || parseFloat(selectedLaminateGroup.min_rate) || 0);
    let calculatedRate = codeRate;
    let unit = 'SHT';
    
    if (codeRateType === 'per sq ft') {
      // Convert size to square feet based on size_unit
      const sqft = convertSizeToSqFt(selectedLaminateSize, codeSizeUnit);
      calculatedRate = codeRate * sqft;
      unit = 'NOS'; // Sold as pieces
    } else if (codeRateType === 'per piece' || codeRateType === 'per dozen' || codeRateType === 'per box') {
      unit = 'NOS';
    } else if (codeRateType === 'per running feet') {
      // For running feet, rate is already per unit length
      calculatedRate = codeRate;
      unit = 'RFT';
    } else if (codeRateType === 'per kg') {
      unit = 'KG';
    } else {
      // per sheet is default
      unit = 'SHT';
    }
    
    // Format display size with unit
    const displaySize = formatSizeWithUnit(selectedLaminateSize, codeSizeUnit);
    
    const laminateProduct = {
      id: variant.id,
      brand_name: selectedLaminateGroup.brand,
      product_name: selectedLaminateGroup.name,
      subcategory: selectedLaminateGroup.name,
      // Combine main product code (prefix) + sub-code for display
      product_code: selectedLaminateGroup.product_code 
        ? `${selectedLaminateGroup.product_code}-${selectedLaminateCode.code}` 
        : selectedLaminateCode.code,
      base_rate: codeRate,
      rate_type: codeRateType,
      size_unit: codeSizeUnit,
      thickness: selectedLaminateGroup.thickness,
      main_category: selectedLaminateGroup.category,
    };
    
    addOrReplaceProduct(laminateProduct, {
      size: displaySize,
      quantity: finalQuantity,
      unit: unit,
      calculatedRate: calculatedRate,
      thickness: selectedLaminateGroup.thickness,
      baseRate: codeRate,
    });
    
    if (!isReplaceMode) {
      toast.success(`Added ${selectedLaminateGroup.brand} ${selectedLaminateGroup.name} (${selectedLaminateCode.code}) to quotation`);
    }
    setSelectedLaminateGroup(null);
    setSelectedLaminateCode(null);
    setSelectedLaminateSize('');
    setLaminateQuantity(1);
    setLaminateQuantityInput('1');
  };

  // Handle hardware group click - open variant selector dialog
  const handleHardwareClick = (group) => {
    setSelectedHardwareGroup(group);
    // Set default variant to first one
    if (group.hardware_variants && group.hardware_variants.length > 0) {
      const firstVariant = group.hardware_variants[0];
      setSelectedHardwareVariant(firstVariant);
      // Set default rate type based on what's available
      if (firstVariant.rate_box) setSelectedHardwareRateType('per box');
      else if (firstVariant.rate_kg) setSelectedHardwareRateType('per kg');
      else if (firstVariant.rate_running_feet) setSelectedHardwareRateType('per running feet');
      else if (firstVariant.rate_dozen) setSelectedHardwareRateType('per dozen');
      else if (firstVariant.rate_piece) setSelectedHardwareRateType('per piece');
    }
    setHardwareQuantity(1);
    setHardwareQuantityInput('1');
  };

  // Get available rate options for a hardware variant
  // getHardwareRateOptions, getHardwareRate, getHardwareUnit now imported from ./productsPage/helpers

  // Handle hardware add to cart
  // ── Hardware multi-select cart helpers ─────────────────────────────────
  // Add the currently-picked size + rate type + quantity to the inner
  // selection cart. Reuses the existing single-variant state so the form
  // resets back to "pick another size" after each add.
  const addToHardwareSelection = () => {
    if (!selectedHardwareGroup || !selectedHardwareVariant || !selectedHardwareRateType) return;

    // Decimal support for weight/length-based rate types (per kg, per running
    // feet, etc.) since hardware is often sold in 2.5 kg, 3.75 kg etc.
    const isFractionalRate = ['per kg', 'per running feet', 'per ltr', 'per gms', 'per ml'].includes(selectedHardwareRateType);
    const parsed = isFractionalRate ? parseFloat(hardwareQuantityInput) : parseInt(hardwareQuantityInput);
    const finalQuantity = Math.max(isFractionalRate ? 0.01 : 1, parsed || 1);
    const rate = getHardwareRate(selectedHardwareVariant, selectedHardwareRateType);
    const unit = getHardwareUnit(selectedHardwareRateType);
    const variant = selectedHardwareGroup.variants[0];

    // De-dupe by size + nail_number + rate type — if the user already picked
    // the same combo, bump the qty instead of pushing a duplicate row.
    const existingIdx = hardwareCart.findIndex(it =>
      it.size === selectedHardwareVariant.size &&
      (it.nail_number || '') === (selectedHardwareVariant.nail_number || '') &&
      it.rateType === selectedHardwareRateType
    );
    if (existingIdx >= 0) {
      const next = [...hardwareCart];
      next[existingIdx] = { ...next[existingIdx], quantity: next[existingIdx].quantity + finalQuantity };
      setHardwareCart(next);
    } else {
      setHardwareCart([...hardwareCart, {
        productId: variant.id,
        size: selectedHardwareVariant.size,
        nail_number: selectedHardwareVariant.nail_number || '',
        rateType: selectedHardwareRateType,
        rate,
        unit,
        quantity: finalQuantity,
      }]);
    }

    // Reset variant/qty so user can immediately pick the next size.
    setSelectedHardwareVariant(null);
    setSelectedHardwareRateType('');
    setHardwareQuantity(1);
    setHardwareQuantityInput('1');
  };

  const removeFromHardwareSelection = (idx) => {
    setHardwareCart(hardwareCart.filter((_, i) => i !== idx));
  };

  // Push every selected hardware variant into the main quotation cart.
  const finalizeHardwareSelection = () => {
    if (!selectedHardwareGroup || hardwareCart.length === 0) return;
    const variant = selectedHardwareGroup.variants[0];
    const isNail = selectedHardwareGroup.hardware_type === 'nail';

    hardwareCart.forEach(item => {
      // For nails (and inch-unit hardware), prepend "14 NO" + add inch symbol
      const sizeWithInch = String(item.size).includes('"') ? item.size : `${item.size}"`;
      const displaySize = (isNail && item.nail_number)
        ? `${item.nail_number} NO - ${sizeWithInch}`
        : (['screw','lipping','fastener','moulding'].includes(selectedHardwareGroup?.hardware_type) ? sizeWithInch : item.size);
      const hardwareProduct = {
        id: variant.id,
        brand_name: selectedHardwareGroup.brand,
        product_name: selectedHardwareGroup.name,
        subcategory: selectedHardwareGroup.name,
        base_rate: item.rate,
        rate_type: item.rateType,
        thickness: displaySize,
        main_category: 'Hardware',
        nail_number: item.nail_number || '',
      };
      addOrReplaceProduct(hardwareProduct, {
        size: displaySize,
        quantity: item.quantity,
        unit: item.unit,
        calculatedRate: item.rate,
        thickness: displaySize,
        baseRate: item.rate,
      });
    });

    if (!isReplaceMode) {
      toast.success(`Added ${hardwareCart.length} item(s) to quotation`);
    }
    setSelectedHardwareGroup(null);
    setSelectedHardwareVariant(null);
    setSelectedHardwareRateType('');
    setHardwareQuantity(1);
    setHardwareQuantityInput('1');
    setHardwareCart([]);
  };

  const handleHardwareAdd = () => {
    // Legacy single-add path — still used by some call sites (e.g. Replace
    // mode where multi-add doesn't make sense). Adds the current selection
    // straight to the quotation without going through the inner cart.
    if (!selectedHardwareGroup || !selectedHardwareVariant || !selectedHardwareRateType) return;
    
    const variant = selectedHardwareGroup.variants[0];
    const isFractionalRate = ['per kg', 'per running feet', 'per ltr', 'per gms', 'per ml'].includes(selectedHardwareRateType);
    const parsed = isFractionalRate ? parseFloat(hardwareQuantityInput) : parseInt(hardwareQuantityInput);
    const finalQuantity = Math.max(isFractionalRate ? 0.01 : 1, parsed || 1);
    const rate = getHardwareRate(selectedHardwareVariant, selectedHardwareRateType);
    const unit = getHardwareUnit(selectedHardwareRateType);
    const isNail = selectedHardwareGroup.hardware_type === 'nail';
    const sizeWithInch = String(selectedHardwareVariant.size).includes('"') ? selectedHardwareVariant.size : `${selectedHardwareVariant.size}"`;
    const displaySize = (isNail && selectedHardwareVariant.nail_number)
      ? `${selectedHardwareVariant.nail_number} NO - ${sizeWithInch}`
      : (['screw','lipping','fastener','moulding'].includes(selectedHardwareGroup.hardware_type) ? sizeWithInch : selectedHardwareVariant.size);
    
    const hardwareProduct = {
      id: variant.id,
      brand_name: selectedHardwareGroup.brand,
      product_name: selectedHardwareGroup.name,
      subcategory: selectedHardwareGroup.name,
      base_rate: rate,
      rate_type: selectedHardwareRateType,
      thickness: displaySize, // Use size as thickness for display
      main_category: 'Hardware',
      nail_number: selectedHardwareVariant.nail_number || '',
    };
    
    addOrReplaceProduct(hardwareProduct, {
      size: displaySize,
      quantity: finalQuantity,
      unit: unit,
      calculatedRate: rate,
      thickness: displaySize,
      baseRate: rate,
    });
    
    if (!isReplaceMode) {
      toast.success(`Added ${selectedHardwareGroup.brand} ${selectedHardwareGroup.name} (${selectedHardwareVariant.size}) to quotation`);
    }
    setSelectedHardwareGroup(null);
    setSelectedHardwareVariant(null);
    setSelectedHardwareRateType('');
    setHardwareQuantity(1);
    setHardwareQuantityInput('1');
  };

  // Handle hardware system group click - open variant selector dialog
  const handleHardwareSystemClick = (group) => {
    setSelectedHardwareSystemGroup(group);
    setSelectedHardwareSystemVariant(null);
    setHardwareSystemQuantity(1);
    setHardwareSystemQuantityInput('1');
    setHardwareSystemCart([]);
    
    // For Tandem, set first available depth as default
    if (group.hardware_system_type === 'tandem' && group.hardware_system_variants?.length > 0) {
      const depths = [...new Set(group.hardware_system_variants.map(v => v.depth).filter(Boolean))];
      if (depths.length > 0) {
        setSelectedTandemDepth(depths[0]);
      }
    } else {
      setSelectedTandemDepth(null);
    }
    
    // For Hinge, reset mount type selection
    if (group.hardware_system_type === 'hinge') {
      const mountTypes = [...new Set(group.hardware_system_variants.map(v => v.hinge_mount).filter(Boolean))];
      if (mountTypes.length > 0) {
        setSelectedHingeMountType(mountTypes[0]);
      } else {
        setSelectedHingeMountType(null);
      }
    } else {
      setSelectedHingeMountType(null);
    }
    
    // For Telescopic, set first available feature type as default
    if (group.hardware_system_type === 'telescopic') {
      const featureTypes = [...new Set(group.hardware_system_variants.map(v => v.feature).filter(Boolean))];
      if (featureTypes.length > 0) {
        setSelectedTelescopicFeature(featureTypes[0]);
      } else {
        setSelectedTelescopicFeature(null);
      }
    } else {
      setSelectedTelescopicFeature(null);
    }
    
    // For Sliding, set first available sliding type as default
    if (group.hardware_system_type === 'sliding') {
      const slidingTypes = [...new Set(group.hardware_system_variants.map(v => v.sliding_type).filter(Boolean))];
      if (slidingTypes.length > 0) {
        setSelectedSlidingType(slidingTypes[0]);
      } else {
        setSelectedSlidingType(null);
      }
    } else {
      setSelectedSlidingType(null);
    }
  };

  // Handle hardware system add to cart
  const handleHardwareSystemAdd = () => {
    if (!selectedHardwareSystemGroup || !selectedHardwareSystemVariant) return;
    
    const variant = selectedHardwareSystemGroup.variants[0];
    const finalQuantity = Math.max(1, parseInt(hardwareSystemQuantityInput) || 1);
    const rate = selectedHardwareSystemVariant.rate || 0;
    
    // Determine unit based on rate_type
    const rateType = selectedHardwareSystemGroup.rate_type || 'per set';
    let unit = 'SET';
    if (rateType.includes('piece')) unit = 'NOS';
    else if (rateType.includes('pair')) unit = 'PAIR';
    else if (rateType.includes('meter')) unit = 'MTR';
    else if (rateType.includes('kg')) unit = 'KG';
    else if (rateType.includes('box')) unit = 'BOX';
    
    // Build description from specs
    const specs = [
      selectedHardwareSystemGroup.load_capacity,
      selectedHardwareSystemGroup.feature,
      selectedHardwareSystemGroup.color,
    ].filter(Boolean).join(' • ');
    
    // Clean up size text - remove leading/trailing x
    let sizeText = selectedHardwareSystemVariant.size_text;
    if (!sizeText) {
      const parts = [selectedHardwareSystemVariant.depth, selectedHardwareSystemVariant.height].filter(Boolean);
      sizeText = parts.join(' x ');
    }
    sizeText = sizeText?.replace(/^x+|x+$/g, '').trim() || '';
    
    const hardwareSystemProduct = {
      id: variant.id,
      brand_name: selectedHardwareSystemGroup.brand,
      product_name: `${selectedHardwareSystemGroup.name}${specs ? ' - ' + specs : ''}`,
      subcategory: selectedHardwareSystemGroup.name,
      base_rate: rate,
      rate_type: rateType,
      thickness: sizeText,
      main_category: 'Hardware',
      hardware_system_type: selectedHardwareSystemGroup.hardware_system_type,
    };
    
    addOrReplaceProduct(hardwareSystemProduct, {
      size: sizeText,
      quantity: finalQuantity,
      unit: unit,
      calculatedRate: rate,
      thickness: sizeText,
      baseRate: rate,
    });
    
    if (!isReplaceMode) {
      toast.success(`Added ${selectedHardwareSystemGroup.brand} ${selectedHardwareSystemGroup.name} (${sizeText}) to quotation`);
    }
    setSelectedHardwareSystemGroup(null);
    setSelectedHardwareSystemVariant(null);
    setHardwareSystemQuantity(1);
    setHardwareSystemQuantityInput('1');
    setHardwareSystemCart([]);
    setSelectedTandemDepth(null);
  };

  // Add item to hardware system cart (multi-select)
  const addToHardwareSystemCart = () => {
    if (!selectedHardwareSystemVariant) return;
    const qty = parseInt(hardwareSystemQuantityInput) || 1;
    
    // Clean up size text
    let sizeText = selectedHardwareSystemVariant.size_text;
    if (!sizeText) {
      const parts = [selectedHardwareSystemVariant.depth, selectedHardwareSystemVariant.height].filter(Boolean);
      sizeText = parts.join(' x ');
    }
    sizeText = sizeText?.replace(/^x+|x+$/g, '').trim() || '';
    
    // Check if already in cart
    const existingIdx = hardwareSystemCart.findIndex(item => item.sizeText === sizeText);
    if (existingIdx >= 0) {
      // Update quantity
      const newCart = [...hardwareSystemCart];
      newCart[existingIdx].quantity += qty;
      setHardwareSystemCart(newCart);
    } else {
      setHardwareSystemCart([...hardwareSystemCart, {
        variant: selectedHardwareSystemVariant,
        sizeText,
        quantity: qty,
        rate: selectedHardwareSystemVariant.rate,
      }]);
    }
    
    // Reset selection for next item
    setSelectedHardwareSystemVariant(null);
    setHardwareSystemQuantityInput('1');
    toast.success(`Added ${sizeText} (${qty} pcs) to selection`);
  };

  // Add all items from hardware system cart to quotation
  const addAllHardwareSystemToQuotation = () => {
    if (hardwareSystemCart.length === 0) return;
    
    const variant = selectedHardwareSystemGroup.variants[0];
    const rateType = selectedHardwareSystemGroup.rate_type || 'per set';
    let unit = 'SET';
    if (rateType.includes('piece')) unit = 'NOS';
    else if (rateType.includes('pair')) unit = 'PAIR';
    else if (rateType.includes('meter')) unit = 'MTR';
    else if (rateType.includes('kg')) unit = 'KG';
    else if (rateType.includes('box')) unit = 'BOX';
    
    const specs = [
      selectedHardwareSystemGroup.load_capacity,
      selectedHardwareSystemGroup.feature,
      selectedHardwareSystemGroup.color,
    ].filter(Boolean).join(' • ');
    
    hardwareSystemCart.forEach(item => {
      const hardwareSystemProduct = {
        id: variant.id,
        brand_name: selectedHardwareSystemGroup.brand,
        product_name: `${selectedHardwareSystemGroup.name}${specs ? ' - ' + specs : ''}`,
        subcategory: selectedHardwareSystemGroup.name,
        base_rate: item.rate,
        rate_type: rateType,
        thickness: item.sizeText,
        main_category: 'Hardware',
        hardware_system_type: selectedHardwareSystemGroup.hardware_system_type,
      };
      
      addItem(hardwareSystemProduct, {
        size: item.sizeText,
        quantity: item.quantity,
        unit: unit,
        calculatedRate: item.rate,
        thickness: item.sizeText,
      });
    });
    
    toast.success(`Added ${hardwareSystemCart.length} item(s) to quotation`);
    setSelectedHardwareSystemGroup(null);
    setSelectedHardwareSystemVariant(null);
    setHardwareSystemQuantity(1);
    setHardwareSystemQuantityInput('1');
    setHardwareSystemCart([]);
    setSelectedTandemDepth(null);
  };

  // Remove item from hardware system cart
  const removeFromHardwareSystemCart = (index) => {
    const newCart = hardwareSystemCart.filter((_, i) => i !== index);
    setHardwareSystemCart(newCart);
  };

  // Handle veneer quick add - opens a dialog to select size
  const [selectedVeneerGroup, setSelectedVeneerGroup] = useState(null);
  const [veneerSelectedSize, setVeneerSelectedSize] = useState('8x4');
  const [veneerSelectedCode, setVeneerSelectedCode] = useState(null); // For multi-code veneers
  const [veneerQuantity, setVeneerQuantity] = useState(1);
  const [veneerQuantityInput, setVeneerQuantityInput] = useState('1');

  const handleVeneerQuickAdd = (group) => {
    // Always open dialog with quantity selector for ALL veneers
    const has8x4 = group.rate_8x4 > 0 || (group.veneer_codes?.some(c => c.rate_8x4 > 0));
    const has10x4 = group.rate_10x4 > 0 || (group.veneer_codes?.some(c => c.rate_10x4 > 0));
    
    if (!has8x4 && !has10x4) {
      toast.error('No rates set for this veneer');
      return;
    }
    
    // Open dialog for quantity selection
    setSelectedVeneerGroup(group);
    // Set default size to the first available
    setVeneerSelectedSize(has8x4 ? '8x4' : '10x4');
    setVeneerQuantity(1);
    setVeneerQuantityInput('1');
    // Set default selected code to first code if multiple codes exist
    if (group.veneer_codes && group.veneer_codes.length > 0) {
      setVeneerSelectedCode(group.veneer_codes[0]);
    } else {
      setVeneerSelectedCode(null);
    }
  };

  const addVeneerToCart = (group, size, quantity, selectedCode = null) => {
    // Use selected code's rate if available, otherwise use group rate
    let ratePerSqft, displayCode, productId, codePrefix;
    
    if (selectedCode) {
      ratePerSqft = size === '8x4' ? selectedCode.rate_8x4 : selectedCode.rate_10x4;
      // Build full code with prefix: LH-EP101
      codePrefix = group.code_prefix || '';
      displayCode = codePrefix && selectedCode.code 
        ? `${codePrefix}-${selectedCode.code}` 
        : selectedCode.code;
      productId = selectedCode.id;
    } else {
      ratePerSqft = size === '8x4' ? group.rate_8x4 : group.rate_10x4;
      codePrefix = group.code_prefix || '';
      displayCode = codePrefix && group.product_code 
        ? `${codePrefix}-${group.product_code}` 
        : group.product_code || '';
      productId = group.variants[0]?.id;
    }
    
    // Calculate sqft per sheet based on size
    const sqftPerSheet = size === '8x4' ? 32 : (size === '10x4' ? 40 : 32);
    // Calculate rate per sheet (rate per sqft × sqft per sheet)
    const ratePerSheet = ratePerSqft * sqftPerSheet;
    
    const variant = group.variants.find(v => v.product_code === (selectedCode?.code || group.product_code)) || group.variants[0];
    
    const veneerProduct = {
      id: productId || variant?.id,
      brand_name: group.brand,
      product_name: group.name,
      subcategory: group.name,
      product_code: displayCode,  // Full code with prefix (LH-EP101)
      species: group.veneer_type || '',  // veneer_type now contains species (Walnut, Teak, etc.)
      base_rate: ratePerSqft,  // Base rate per sqft (not calculated per sheet)
      rate_type: 'per sq ft',
      thickness: size,
      main_category: 'Veneers',
    };
    
    addOrReplaceProduct(veneerProduct, {
      size: size,  // Just size without sqft calculation
      quantity: quantity,
      unit: 'SHEET',
      calculatedRate: ratePerSheet, // Calculated rate per sheet for amount
      baseRate: ratePerSqft,  // Original base rate per sqft for display
      thickness: size,
    });
    
    if (!isReplaceMode) {
      const codeText = displayCode ? ` [${displayCode}]` : '';
      toast.success(`Added ${group.brand} ${group.name}${codeText} (${size}) - ${quantity} sheets to quotation`);
    }
    setSelectedVeneerGroup(null);
    setVeneerSelectedCode(null);
  };

  // Quick Add Natural Veneer — identical math to the cart handler
  // (uom controls the rate × area expansion), plus optional Save &
  // Remember into the Veneers catalog (veneers now save too).
  const handleQuickVeneerSubmit = async () => {
    const rate = parseFloat(quickVeneerData.rate);
    const qty = parseFloat(quickVeneerData.quantity) || 1;
    const uom = quickVeneerData.uom || 'sqft';

    addQuickVeneer({
      species: quickVeneerData.species,
      product_code: quickVeneerData.product_code || '',
      size: quickVeneerData.size,
      rate: rate,
      quantity: qty,
      uom,
      unit: quickVeneerData.unit,
      show_brand: quickVeneerData.show_brand !== false,
      main_category: 'Veneers',
    });

    if (quickVeneerData.saveToDatabase) {
      const species = (quickVeneerData.species || '').trim();
      try {
        await api.quickAddProduct({
          brand_name: species.toUpperCase(),
          product_name: 'Natural',
          subcategory: 'Natural',
          product_code: (quickVeneerData.product_code || '').toUpperCase(),
          veneer_type: species,
          base_rate: rate,
          rate_type: uom === 'sqft' ? 'per sq ft' : (uom === 'piece' ? 'per piece' : 'per sheet'),
          rate_8x4: uom === 'sqft' ? rate : 0,
          main_category: 'Veneers',
          available_sizes: [stripSizeUnit(quickVeneerData.size) || '8x4'],
          status: 'active',
          quick_added: true,
        });
        toast.success(`"${species}" veneer saved! Will appear in the Veneers catalog.`);
        fetchProducts();
      } catch (err) {
        console.error('Failed to save veneer:', err);
        toast.error('Added to quotation, but saving to catalog failed');
      }
    }

    toast.success(`Added ${quickVeneerData.species} veneer to quotation`);
    setShowQuickVeneerDialog(false);
    setQuickVeneerData({ ...DEFAULT_CART_VENEER_QUICK_ADD, saveToDatabase: true });
  };

  // Handle custom quick add
  const handleCustomQuickAdd = () => {
    if (!quickAddData.name || !quickAddData.rate) {
      toast.error('Name and Rate are required');
      return;
    }
    
    const baseRate = parseFloat(quickAddData.rate) || 0;
    
    // Get the actual rate type (custom or predefined)
    const actualRateType = quickAddData.rate_type === 'custom' 
      ? quickAddData.custom_rate_type 
      : quickAddData.rate_type;
    
    // Map rate_type to appropriate unit
    const unitMap = {
      'per piece': 'NOS',
      'per sheet': 'SHEET',
      'per sq ft': 'NOS',  // For sq ft items, unit is NOS (number of sheets/pieces)
      'per dozen': 'DZN',
      'per kg': 'KG',
      'per running ft': 'RFT',
      'per box': 'BOX',
      'per set': 'SET',
      'per bundle': 'BUNDLE',
    };
    
    // Calculate rate per unit based on rate_type
    let calculatedRate = baseRate;
    let sizeValue = '';
    
    if (actualRateType === 'per sq ft') {
      // Calculate sq ft from custom dimensions
      const sqft = calculateSqFt();
      calculatedRate = baseRate * sqft;  // Rate per sheet
      sizeValue = `${quickAddData.sizeLength}x${quickAddData.sizeWidth}`;
    }
    
    // Get the selected category. When the user picks the generic "Custom/Other"
    // option we normalize to 'Others' — that's the canonical category the
    // PDF/backend understands (see backend/services/pdf_service.normalize_category).
    // Using a stray 'Custom' value would drop cascading discount rendering and
    // send the item to an unlabelled "CUSTOM" section header on the PDF.
    const rawCategory = quickAddData.category || 'Others';
    const selectedCategory = (rawCategory === 'Custom' || !rawCategory) ? 'Others' : rawCategory;
    
    const customProduct = {
      id: `custom-${Date.now()}`,
      brand_name: quickAddData.name,  // Just the product name, no "Custom" prefix
      product_name: quickAddData.name,
      product_code: quickAddData.product_code || '',  // Optional code for customer PDF
      subcategory: quickAddData.specs || '',
      base_rate: baseRate,  // Store the original base rate
      rate_type: actualRateType,
      thickness: '',
      grade: '',
      main_category: selectedCategory,  // Use selected category
    };
    
    addItem(customProduct, {
      size: sizeValue,
      quantity: quickAddData.quantity,
      unit: unitMap[actualRateType] || 'NOS',
      calculatedRate: calculatedRate,  // Rate per unit (e.g., per sheet for sq ft items)
      thickness: '',
      // Quick-add items must be editable long after save.
      // Marking is_quick_add=true lets the cart's pencil-edit button
      // appear when the quotation is re-opened for edit days later.
      is_quick_add: true,
    });
    
    toast.success(`Added "${quickAddData.name}" to quotation`);
    setShowQuickAdd(false);
    setQuickAddData({ name: '', product_code: '', rate: '', rate_type: 'per piece', custom_rate_type: '', category: 'Others', sizeLength: '8', sizeWidth: '4', sizeUnit: 'feet', specs: '', quantity: 1, quantityInput: '1' });
  };

  // Laminate/Highlighter Quick Add — SAME composition + math as the cart
  // handler (description → code → thickness, brand honours the PDF
  // toggle), plus optional Save & Remember into the catalog.
  const handleLaminateQuickAdd = async () => {
    const { brand, product_code, description, thickness, rate, size, category, quantity, uom, show_brand, saveToDatabase } = laminateQuickAddData;
    const baseRate = parseFloat(rate) || 0;
    const qty = parseFloat(quantity) || 1;
    if (saveToDatabase && !brand) {
      toast.error('Brand is required when Save & Remember is on');
      return;
    }
    const composedName = joinWithHyphen(description, product_code, thickness);
    const species = (show_brand !== false)
      ? joinWithHyphen(brand, composedName)
      : composedName;
    addQuickVeneer({
      species,
      product_code: product_code.toUpperCase(),
      size: size,
      rate: baseRate,
      quantity: qty,
      unit: (uom === 'sqft') ? 'NOS' : (uom === 'piece' ? 'NOS' : 'SHEET'),
      uom: uom || 'sheet',
      main_category: category,
      brand,
      finish: description || '',
      description_text: description || '',
      thickness: thickness || '',
      show_brand: show_brand !== false,
    });

    if (saveToDatabase) {
      try {
        await api.quickAddProduct({
          brand_name: brand.toUpperCase(),
          product_name: product_code.toUpperCase(),
          product_code: product_code.toUpperCase(),
          subcategory: description || '',
          grade: description || '',
          thickness: thickness || '',
          base_rate: baseRate,
          rate_type: uom === 'sqft' ? 'per sq ft' : (uom === 'piece' ? 'per piece' : 'per sheet'),
          main_category: category,
          available_sizes: [stripSizeUnit(size) || '8x4'],
          status: 'active',
          quick_added: true,
        });
        toast.success(`"${product_code}" saved! Will appear in search.`);
        fetchProducts();
      } catch (err) {
        console.error('Failed to save product:', err);
        toast.error('Added to quotation, but saving to catalog failed');
      }
    }

    toast.success(`Added ${product_code} ${category.slice(0, -1)} to quotation`);
    setShowLaminateQuickAdd(false);
    setLaminateQuickAddData({ ...DEFAULT_CART_LAMINATE_QUICK_ADD, saveToDatabase: true });
  };

  // Hardware Quick Add — same composition as the cart handler, plus
  // optional Save & Remember into the catalog.
  const handleHardwareQuickAdd = async () => {
    const { brand, product_name, product_code, specs, size, rate, rate_type, quantity, show_brand, saveToDatabase } = hardwareQuickAddData;
    const baseRate = parseFloat(rate) || 0;
    const qty = parseFloat(quantity) || 1;
    const species = (show_brand !== false)
      ? joinWithHyphen(brand, product_name, specs)
      : joinWithHyphen(product_name, specs);
    addQuickVeneer({
      species,
      product_code: product_code || '',
      size: (size || '').trim() || specs || '',
      rate: baseRate,
      quantity: qty,
      unit: rate_type.replace('per ', '').toUpperCase(),
      main_category: 'Hardware',
      brand,
      show_brand: show_brand !== false,
    });

    if (saveToDatabase) {
      try {
        await api.quickAddProduct({
          brand_name: brand.toUpperCase(),
          product_name: product_name,
          product_code: product_code || '',
          subcategory: specs || product_name,
          thickness: specs || '',
          base_rate: baseRate,
          rate_type: rate_type,
          main_category: 'Hardware',
          status: 'active',
          quick_added: true,
        });
        toast.success(`"${product_name}" saved to catalog!`);
        fetchProducts();
      } catch (err) {
        console.error('Failed to save product:', err);
        toast.error('Added to quotation, but saving to catalog failed');
      }
    }

    toast.success(`Added ${product_name} to quotation`);
    setShowHardwareQuickAdd(false);
    setHardwareQuickAddData({ ...DEFAULT_CART_HARDWARE_QUICK_ADD, saveToDatabase: true });
  };

  // Adhesive Quick Add — same composition as the cart handler, plus
  // optional Save & Remember into the catalog.
  const handleAdhesiveQuickAdd = async () => {
    const { brand, product_name, product_code, weight, size, rate, quantity, show_brand, saveToDatabase } = adhesiveQuickAddData;
    const baseRate = parseFloat(rate) || 0;
    const qty = parseFloat(quantity) || 1;
    const species = (show_brand !== false)
      ? joinWithHyphen(brand, product_name)
      : (product_name || '');
    addQuickVeneer({
      species,
      product_code: product_code || '',
      size: (size || '').trim() || weight,
      rate: baseRate,
      quantity: qty,
      unit: weight.includes('LTR') ? 'LTR' : 'KG',
      main_category: 'Adhesives',
      brand,
      show_brand: show_brand !== false,
    });

    if (saveToDatabase) {
      try {
        await api.quickAddProduct({
          brand_name: brand.toUpperCase(),
          product_name: product_name,
          product_code: product_code || '',
          subcategory: product_name,
          thickness: weight,
          base_rate: baseRate,
          rate_type: 'per kg',
          main_category: 'Adhesives',
          status: 'active',
          quick_added: true,
        });
        toast.success(`"${product_name} ${weight}" saved to catalog!`);
        fetchProducts();
      } catch (err) {
        console.error('Failed to save product:', err);
        toast.error('Added to quotation, but saving to catalog failed');
      }
    }

    toast.success(`Added ${brand} ${product_name} ${weight} to quotation`);
    setShowAdhesiveQuickAdd(false);
    setAdhesiveQuickAddData({ ...DEFAULT_CART_ADHESIVE_QUICK_ADD, saveToDatabase: true });
  };

  // Get thickness options based on product type
  const getThicknessOptions = (group) => {
    if (group.isBlockboard) {
      return BLOCKBOARD_THICKNESSES;
    }
    if (group.isDoor) {
      return DOOR_THICKNESSES;
    }
    return STANDARD_THICKNESSES;
  };

  // Get size options based on product type
  const getSizeOptions = (group) => {
    if (group.isDoor) {
      return DOOR_SIZE_OPTIONS;
    }
    return SIZE_OPTIONS;
  };

  const hasFilters = search || filterBrand || filterCategory || filterProductId;
  const isPlywoodCategory = filterCategory === 'Plywood' || filterCategory === '';

  // Helper function to handle product click and open appropriate dialog
  // (Dead helper removed — `handleProductClick` referenced setters that
  // never existed in the refactored ProductsPage and was never invoked.
  // ESLint pre-commit gate (iter72+) flagged it via no-undef.)

  return (
    <div data-testid="products-page">
      {/* Replace Mode Banner */}
      {isReplaceMode && (
        <div className="bg-heda-gold-tint border border-heda-gold/30 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="replace-mode-banner">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-heda-gold" />
            <div>
              <p className="text-sm font-semibold text-heda-ink">Replace Mode</p>
              <p className="text-xs text-heda-mute">Select a product to replace the item in your quotation. Filtered to: <strong>{replaceCategoryFilter}</strong></p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={navigateBackToCart}
            className="rounded-xl border-red-200 text-red-700 hover:bg-red-50 h-8 text-xs"
            data-testid="cancel-replace-btn"
          >
            Cancel Replace
          </Button>
        </div>
      )}
      
      {/* Header - Mobile responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-[20px] font-bold text-heda-ink">{isReplaceMode ? 'Select Replacement Product' : 'Products'}</h1>
          <p className="text-xs sm:text-sm text-heda-sub">{total} products • {groupedProducts.length} groups</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Header-bar Quick Add dropdown extracted
              to ./productsPage/components/QuickAddMenu — pure JSX with
              6 callback props, so the parent's state setters stay
              private. */}
          <QuickAddMenu
            onCustomItem={() => setShowQuickAdd(true)}
            onAddLaminate={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Laminates', saveToDatabase: true })); setShowLaminateQuickAdd(true); }}
            onAddHighlighter={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Highlighters', saveToDatabase: true })); setShowLaminateQuickAdd(true); }}
            onAddHardware={() => { setHardwareQuickAddData(d => ({ ...d, saveToDatabase: true })); setShowHardwareQuickAdd(true); }}
            onAddAdhesive={() => { setAdhesiveQuickAddData(d => ({ ...d, saveToDatabase: true })); setShowAdhesiveQuickAdd(true); }}
            onAddVeneer={() => setShowQuickVeneerDialog(true)}
          />
          <ViewToggleAndCart
            viewMode={viewMode}
            onSetViewMode={setViewMode}
            cartCount={cartItems.length}
            onGoToCart={() => navigate('/cart')}
          />
        </div>
      </div>

      {/* Search - Mobile responsive */}
      <form onSubmit={handleSearch} className="mb-3 sm:mb-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute z-10" />
            <Input
              ref={searchInputRef}
              value={searchInput}
              onChange={(e) => handleSearchInput(e.target.value)}
              onFocus={() => searchInput.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder='Search by name, code, size...'
              className="rounded-xl pl-9 sm:pl-10 bg-white border-heda-line focus:border-heda-gold text-sm sm:text-base h-10 sm:h-11"
              data-testid="products-search-input"
            />
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-heda-line rounded-xl shadow-lg max-h-60 sm:max-h-80 overflow-auto">
                {searchLoading && (
                  <div className="px-3 py-2 text-xs sm:text-sm text-heda-mute">Searching...</div>
                )}
                {suggestions.map((s, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 sm:py-3 cursor-pointer hover:bg-heda-cream/60 border-b border-heda-line last:border-0"
                    onMouseDown={() => selectSuggestion(s)}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-heda-ink text-sm truncate">
                          {s.brand} {s.name && `- ${s.name}`}
                        </p>
                        <p className="text-xs text-heda-sub truncate">
                          {s.grade && <span className="mr-2">{s.grade}</span>}
                          <span className="text-heda-mute">{s.variants.length} variant{s.variants.length > 1 ? 's' : ''}</span>
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {s.product_code && (
                          <Badge variant="outline" className="rounded-full text-[10px] border-heda-gold text-heda-gold font-mono">
                            {s.product_code}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button type="submit" className="rounded-xl bg-heda-ink hover:bg-black text-white h-10 sm:h-11 px-3 sm:px-6 text-sm" data-testid="products-search-btn">
            <Search className="w-4 h-4 sm:hidden" />
            <span className="hidden sm:inline">Search</span>
          </Button>
          {/* Removed the non-functional Filter toggle button
              (data-testid="products-filter-btn") and its dead handler.
              Category chips (below) already own category filtering; the
              brand/subcategory panel it toggled was unreachable in
              practice and reported broken. */}
        </div>
      </form>

      {/* Category Quick Filters — chips + category-scoped Quick Add CTAs.
          Extracted to
          frontend/src/pages/productsPage/components/CategoryFilterChips
          — pure JSX + 5 props, easy to unit-test in isolation. */}
      <CategoryFilterChips
        filterCategory={filterCategory}
        onSelectCategory={(cat) => { setFilterCategory(cat); setPage(1); }}
        onQuickAddVeneer={() => setShowQuickVeneerDialog(true)}
        onQuickAddLaminate={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Laminates' })); setShowLaminateQuickAdd(true); }}
        onQuickAddHighlighter={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Highlighters' })); setShowLaminateQuickAdd(true); }}
      />

      {/* Filter Panel removed — its toggle button was
          reported broken/redundant; category chips (above) already own
          category filtering, and brand filtering flows via the URL
          `?brand=` param set by other entry points. */}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-heda-mute"><Loader2 className="h-5 w-5 animate-spin text-heda-gold" /> Loading products...</div>
      )}

      {/* Card View - Grouped Products */}
      {!loading && viewMode === 'cards' && (
        <>
          {groupedProducts.length === 0 ? (
            <div className="heda-card text-center py-12">
              <div className="text-heda-mute mb-4">
                {search ? `No products found for "${search}"` : 'No products found'}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <span className="text-sm text-heda-sub">Quick Add:</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Laminates', product_code: search, saveToDatabase: true })); setShowLaminateQuickAdd(true); }}
                >
                  <Palette className="w-4 h-4 mr-1" /> Laminate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Highlighters', product_code: search, saveToDatabase: true })); setShowLaminateQuickAdd(true); }}
                >
                  <Sparkles className="w-4 h-4 mr-1" /> Highlighter
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl border-heda-sub text-heda-sub"
                  onClick={() => { setHardwareQuickAddData(d => ({ ...d, product_code: search, saveToDatabase: true })); setShowHardwareQuickAdd(true); }}
                >
                  <Wrench className="w-4 h-4 mr-1" /> Hardware
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => { setAdhesiveQuickAddData(d => ({ ...d, product_code: search, saveToDatabase: true })); setShowAdhesiveQuickAdd(true); }}
                >
                  <Pipette className="w-4 h-4 mr-1" /> Adhesive
                </Button>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedProducts.map((group, idx) => (
              <Card 
                key={idx} 
                className="rounded-2xl bg-white shadow-none border-heda-line hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                data-testid={`product-group-${idx}`}
              >
              <CardContent className="p-4 flex flex-col flex-1">
                {/* Card header (brand + specs badges +
                    category badge) extracted to ProductCardHeader.
                    Pure JSX with only `group` as prop. */}
                <ProductCardHeader group={group} />

                {/* Variant chip selector (both weight
                    and thickness-bulk) extracted to VariantChipSelector.
                    The two blocks used to be duplicated with subtly
                    different sort rules — one place now. */}
                {group.isWeightBulk && (
                  <VariantChipSelector group={group} mode="weight" onSelect={handleThicknessSelect} />
                )}
                {group.isThicknessBulk && !group.isVeneer && (
                  <VariantChipSelector group={group} mode="thickness" onSelect={handleThicknessSelect} />
                )}

                {/* LAMINATES / HIGHLIGHTERS / ACP / WOODEN FLOORING — single source of truth: either code selector OR direct add (never both) */}
                {(group.category === 'Laminates' || group.category === 'Highlighters' || group.isLaminateWithCodes) && (
                  <LaminateBlock
                    group={group}
                    idx={idx}
                    onSelectCode={handleLaminateClick}
                    onDirectAdd={handleDirectQuickAdd}
                  />
                )}

                {/* HARDWARE products with variants - Special display */}
                {group.isHardware && (
                  <HardwareVariantsBlock group={group} idx={idx} onSelectCategory={handleHardwareClick} />
                )}

                {/* NEW HARDWARE SYSTEM products (Tandems, Hinges, Slides) - Special display */}
                {group.isHardwareSystem && (
                  <HardwareSystemBlock group={group} idx={idx} onSelect={handleHardwareSystemClick} />
                )}

                {/* VENEER products - Compact display */}
                {group.isVeneer && (
                  <VeneerBlock group={group} idx={idx} onQuickAdd={handleVeneerQuickAdd} />
                )}

                {/* For non-thickness/weight bulk categories (excluding laminates, hardware, veneers): Show variants directly */}
                {!group.isThicknessBulk && !group.isWeightBulk && !group.isHardware && !group.isHardwareSystem && !group.isVeneer && !group.isLaminateWithCodes && group.category !== 'Laminates' && group.category !== 'Highlighters' && (
                  <DefaultCardVariantsPreview group={group} />
                )}

                {/* Single-variant Add button */}
                {!group.isThicknessBulk && !group.isWeightBulk && !group.isHardware && !group.isHardwareSystem && !group.isVeneer && !group.isLaminateWithCodes && group.category !== 'Laminates' && group.category !== 'Highlighters' && group.variants.length === 1 && (
                  <DefaultCardSingleAddButton group={group} idx={idx} onDirectAdd={handleDirectQuickAdd} />
                )}

                {/* Multi-variant Add buttons */}
                {!group.isThicknessBulk && !group.isWeightBulk && !group.isHardware && !group.isHardwareSystem && !group.isVeneer && !group.isLaminateWithCodes && group.category !== 'Laminates' && group.category !== 'Highlighters' && group.variants.length > 1 && (
                  <DefaultCardMultiVariantButtons group={group} onDirectAdd={handleDirectQuickAdd} />
                )}
              </CardContent>
            </Card>
          ))}
          </div>
          )}
        </>
      )}

      {/* Table View — extracted to
          frontend/src/pages/productsPage/components/ProductsTableView */}
      {!loading && viewMode === 'table' && (
        <ProductsTableView
          products={products}
          search={search}
          onAddProduct={(p) => {
            setSelectedGroup({
              brand: p.brand_name,
              name: p.subcategory,
              variants: [p],
              thicknessMap: { [p.thickness]: p },
              category: p.main_category,
              rate_type: p.rate_type,
            });
            setSelectedThickness(p.thickness);
          }}
          onQuickAddLaminate={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Laminates', product_code: search, saveToDatabase: true })); setShowLaminateQuickAdd(true); }}
          onQuickAddHighlighter={() => { setLaminateQuickAddData(d => ({ ...d, category: 'Highlighters', product_code: search, saveToDatabase: true })); setShowLaminateQuickAdd(true); }}
          onQuickAddHardware={() => { setHardwareQuickAddData(d => ({ ...d, product_code: search, saveToDatabase: true })); setShowHardwareQuickAdd(true); }}
          onQuickAddAdhesive={() => { setAdhesiveQuickAddData(d => ({ ...d, product_code: search, saveToDatabase: true })); setShowAdhesiveQuickAdd(true); }}
        />
      )}

      {/* Pagination - extra bottom padding keeps it clear of the mobile bottom nav */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 mb-16 pb-4">
          <p className="text-sm text-heda-sub">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl h-10 px-4 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold">
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="rounded-xl h-10 px-4 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Quick Add Dialog - Size & Quantity Selection */}
      <VariantQuickAddDialog
        selectedGroup={selectedGroup}
        selectedThickness={selectedThickness}
        onClose={() => { setSelectedGroup(null); setSelectedThickness(null); setQuantityInput('1'); setQuantity(1); setAdhesiveRateChoice('primary'); }}
        getProductForThickness={getProductForThickness}
        getSizeOptions={getSizeOptions}
        getCalculatedRate={getCalculatedRate}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        quantityInput={quantityInput}
        setQuantityInput={setQuantityInput}
        setQuantity={setQuantity}
        adhesiveRateChoice={adhesiveRateChoice}
        setAdhesiveRateChoice={setAdhesiveRateChoice}
        onAdd={handleQuickAdd}
      />

      {/* Custom Quick Add Dialog */}
      <CustomQuickAddDialog
        open={showQuickAdd}
        onOpenChange={setShowQuickAdd}
        data={quickAddData}
        setData={setQuickAddData}
        categories={categories}
        onSubmit={handleCustomQuickAdd}
      />

      {/* Laminate/Highlighter Quick Add — SAME dialog as the cart page,
          with the Save & Remember checkbox enabled. */}
      <QuickAddLaminateDialog
        open={showLaminateQuickAdd}
        onOpenChange={setShowLaminateQuickAdd}
        data={laminateQuickAddData}
        setData={setLaminateQuickAddData}
        onAdd={handleLaminateQuickAdd}
        saveRemember={{
          checked: laminateQuickAddData.saveToDatabase,
          onChange: (v) => setLaminateQuickAddData(d => ({ ...d, saveToDatabase: v })),
        }}
      />

      {/* Hardware Quick Add — same dialog as cart + Save & Remember */}
      <QuickAddHardwareDialog
        open={showHardwareQuickAdd}
        onOpenChange={setShowHardwareQuickAdd}
        data={hardwareQuickAddData}
        setData={setHardwareQuickAddData}
        onAdd={handleHardwareQuickAdd}
        saveRemember={{
          checked: hardwareQuickAddData.saveToDatabase,
          onChange: (v) => setHardwareQuickAddData(d => ({ ...d, saveToDatabase: v })),
        }}
      />

      {/* Adhesive Quick Add — same dialog as cart + Save & Remember */}
      <QuickAddAdhesiveDialog
        open={showAdhesiveQuickAdd}
        onOpenChange={setShowAdhesiveQuickAdd}
        data={adhesiveQuickAddData}
        setData={setAdhesiveQuickAddData}
        onAdd={handleAdhesiveQuickAdd}
        saveRemember={{
          checked: adhesiveQuickAddData.saveToDatabase,
          onChange: (v) => setAdhesiveQuickAddData(d => ({ ...d, saveToDatabase: v })),
        }}
      />

      {/* Plywood Multi-Size Selection Dialog */}
      <PlywoodMultiSelectDialog
        open={showPlywoodMultiSelect}
        onClose={closePlywoodMultiSelect}
        selectedGroup={selectedGroup}
        product={plywoodMultiSelectProduct}
        thickness={plywoodMultiSelectThickness}
        sizeOptions={SIZE_OPTIONS}
        selectedSize={plywoodSelectedSize}
        setSelectedSize={setPlywoodSelectedSize}
        quantityInput={plywoodQuantityInput}
        setQuantityInput={setPlywoodQuantityInput}
        stockInfo={plywoodStockInfo}
        cart={plywoodCart}
        onAddToCart={addToPlywoodCart}
        onRemoveFromCart={removeFromPlywoodCart}
        onMultiAdd={handlePlywoodMultiAdd}
        onSingleAdd={handlePlywoodSingleAdd}
      />

      {/* Laminate Code Selector Dialog */}
      <LaminateCodeSelectorDialog
        group={selectedLaminateGroup}
        onClose={() => { setSelectedLaminateGroup(null); setSelectedLaminateCode(null); setLaminateCodeSearch(''); }}
        codeSearch={laminateCodeSearch}
        setCodeSearch={setLaminateCodeSearch}
        selectedCode={selectedLaminateCode}
        onSelectCode={handleLaminateCodeSelect}
        selectedSize={selectedLaminateSize}
        setSelectedSize={setSelectedLaminateSize}
        quantity={laminateQuantity}
        setQuantity={setLaminateQuantity}
        quantityInput={laminateQuantityInput}
        setQuantityInput={setLaminateQuantityInput}
        onAdd={handleLaminateAdd}
      />

      {/* Hardware Variant Selector Dialog */}
      <HardwareVariantSelectorDialog
        group={selectedHardwareGroup}
        onClose={() => { setSelectedHardwareGroup(null); setSelectedHardwareVariant(null); setSelectedHardwareRateType(''); setHardwareCart([]); }}
        selectedVariant={selectedHardwareVariant}
        setSelectedVariant={setSelectedHardwareVariant}
        selectedRateType={selectedHardwareRateType}
        setSelectedRateType={setSelectedHardwareRateType}
        quantityInput={hardwareQuantityInput}
        setQuantityInput={setHardwareQuantityInput}
        setQuantity={setHardwareQuantity}
        cart={hardwareCart}
        onAddToSelection={addToHardwareSelection}
        onRemoveFromSelection={removeFromHardwareSelection}
        onFinalize={finalizeHardwareSelection}
      />

      {/* Hardware System Variant Selector Dialog (Tandems, Hinges, etc.) */}
      <HardwareSystemSelectorDialog
        group={selectedHardwareSystemGroup}
        onClose={() => { setSelectedHardwareSystemGroup(null); setHardwareSystemCart([]); setSelectedTandemDepth(null); setSelectedTelescopicFeature(null); setSelectedSlidingType(null); }}
        selectedVariant={selectedHardwareSystemVariant}
        setSelectedVariant={setSelectedHardwareSystemVariant}
        tandemDepth={selectedTandemDepth}
        setTandemDepth={setSelectedTandemDepth}
        hingeMountType={selectedHingeMountType}
        setHingeMountType={setSelectedHingeMountType}
        telescopicFeature={selectedTelescopicFeature}
        setTelescopicFeature={setSelectedTelescopicFeature}
        slidingType={selectedSlidingType}
        setSlidingType={setSelectedSlidingType}
        quantityInput={hardwareSystemQuantityInput}
        setQuantityInput={setHardwareSystemQuantityInput}
        cart={hardwareSystemCart}
        onAddToCart={addToHardwareSystemCart}
        onRemoveFromCart={removeFromHardwareSystemCart}
        onAddAll={addAllHardwareSystemToQuotation}
        onSingleAdd={handleHardwareSystemAdd}
      />

      {/* Veneer Size Selector Dialog */}
      <VeneerSizeSelectorDialog
        group={selectedVeneerGroup}
        onClose={() => { setSelectedVeneerGroup(null); setVeneerSelectedCode(null); }}
        selectedCode={veneerSelectedCode}
        setSelectedCode={setVeneerSelectedCode}
        selectedSize={veneerSelectedSize}
        setSelectedSize={setVeneerSelectedSize}
        quantityInput={veneerQuantityInput}
        setQuantityInput={setVeneerQuantityInput}
        setQuantity={setVeneerQuantity}
        onAdd={addVeneerToCart}
      />

      {/* Quick Add Veneer — SAME dialog as the cart page, with Save &
          Remember enabled (fixes: veneers never saved from Products). */}
      <QuickAddVeneerDialog
        open={showQuickVeneerDialog}
        onOpenChange={setShowQuickVeneerDialog}
        data={quickVeneerData}
        setData={setQuickVeneerData}
        onAdd={handleQuickVeneerSubmit}
        saveRemember={{
          checked: quickVeneerData.saveToDatabase,
          onChange: (v) => setQuickVeneerData(d => ({ ...d, saveToDatabase: v })),
        }}
      />
    </div>
  );
}
